import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { fileStatus } from 'components/Recommendations/Campaign/Media/media'
import { CancelledIcon } from 'images'
import MediaLabel from 'components/Shared/MediaCard/MediaLabel'
import CustomRadioButton from 'components/Shared/CustomRadioButton'
import CardContent from './CardContent'
import { DotsMenuIcon, CheckIcon, TriangleAlertIcon } from 'images'
import ActionMenu from 'components/Shared/MediaCard/ActionMenu'
import ProgressBar from 'components/Shared/MediaCard/ProgressBar'
import LoaderSpinner from 'components/Shared/Loader/LoaderSpinner'

const MediaPreview = ({ file }) => {
  if (file.youtube_video_id) {
    if (!file.thumbnail_url) return null

    return (
      <img
        className="media-list-card__preview-media"
        src={file.thumbnail_url}
        alt={file.title || file.filename}
      />
    )
  }

  const src = file.preview || file.url

  if (file.type?.startsWith('video')) {
    return <video className="media-list-card__preview-media" controls={false} src={src} title={file.name || file.filename} />
  }

  return <img className="media-list-card__preview-media" src={src} alt={file.name || file.filename} />
}

export default function MediaListCard(props) {
  const {
    item,
    selectiveMode = false,
    disableSelection = false,
    isSelected = false,
    onSelect = () => {},
    isReady = false,
    onPreviewClick,
    onViewVariants,
    showLabel = false,
    mediaLabel,
    onRemove,
    onRemoveSubGroup,
    subGroupItems,
    subGroupId,
    originalId,
    originalMedia,
    isCampaignRunning = false,
    readonly = false,
    onAddToSlot,
    onAddLibraryToSlot,
    onReplaceLibraryToSlot,
    onReplaceInSlot,
    onRemoveFromSlot,
    setSlotError,
    requiredSlotRatios,
    isGoogleOnly = false,
    isGoogleSelected = false,
    googleExactRatios = [],
    showValidationErrors = false,
    missingGoogleExactRatios = [],
  } = props

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuContainerRef = useRef(null)

  const isError = item.status === fileStatus.uploadFailed
  const isUploading = item.status === fileStatus.new || item.status === fileStatus.uploading
  const isGenerating = Boolean(item.generating)
  const isYoutube = Boolean(item.youtube_video_id)
  const isHydrating = isYoutube && item.title === undefined

  const { t } = useTranslation()

  const handlePreviewClick = () => {
    onPreviewClick?.(item)
  }

  const handleViewVariantsClick = () => {
    onViewVariants?.(item)
  }

  const handleToggleSelection = () => {
    if (disableSelection) {
      return
    }

    onSelect()
  }

  const actionMenuItems = [
    ...(onPreviewClick && !isYoutube ? [{
      key: 'addVariants',
      label: t('Recommendations.campaign.setup.media.dropdown.addVariants'),
      onClick: onPreviewClick,
    }] : []),
    ...(onRemoveSubGroup ? [{
      key: 'remove',
      label: t('Recommendations.campaign.setup.media.dropdown.remove'),
      onClick: onRemoveSubGroup,
    }] : []),
  ]

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (menuContainerRef.current?.contains(event.target)) {
        return
      }

      setIsMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  const canPreview = !isGenerating && !isUploading && !isHydrating && !selectiveMode && onPreviewClick
  const warnings = isYoutube ? item.warnings ?? [] : []
  const canViewVariants = isGenerating && isReady && onViewVariants
  const isPreviewClickable = canPreview || canViewVariants

  return (
    <div
      className={classnames('media-list-card', {
        'media-list-card--selected': isSelected,
        'media-list-card--selective': selectiveMode,
        'media-list-card--selection-disabled': selectiveMode && disableSelection,
      })}
    >
      <div
        className={classnames('media-list-card__preview', {
          'media-list-card__preview--clickable': isPreviewClickable,
        })}
        onClick={isPreviewClickable ? (canViewVariants ? handleViewVariantsClick : handlePreviewClick) : undefined}
      >
        <MediaPreview file={item} />

        {isError && <div className="error-overlay">
          <div className="icon">
            <CancelledIcon width={12} height={12} />
          </div>
          <p className="title">{t('common.genericError')}</p>
        </div>}

        {isUploading && <div className="uploading-overlay">
          <ProgressBar progress={item.progress ?? 0} />
        </div>}

        {isHydrating && <div className="uploading-overlay">
          <LoaderSpinner size="tiny" />
        </div>}

        {warnings.length > 0 && !isHydrating && (
          <div className="media-warning-badge media-warning-badge--overlay" title={warnings.join('\n')}>
            <TriangleAlertIcon width={16} height={16} />
          </div>
        )}

        {showLabel && mediaLabel && !isError && !isUploading && !isGenerating && !isHydrating && (
          <MediaLabel
            variant={mediaLabel.variant}
            headline={mediaLabel.headline}
            bothSelected={mediaLabel.bothSelected}
            position={'bottom-left'}
          />
        )}

        {isGenerating && <div className="generating-overlay">
          {isReady ? <CheckIcon width={24} height={24} /> : <LoaderSpinner size="tiny" />}
          <span className="title">{isReady ? t('Recommendations.campaign.setup.media.coCreateVariants.ready') : t('Recommendations.campaign.setup.media.coCreateVariants.inProgress')}</span>
        </div>}
      </div>

      {!isYoutube && (
        <CardContent
          onRemove={onRemove}
          isError={isError}
          subGroupItems={subGroupItems}
          subGroupId={subGroupId}
          originalId={originalId}
          originalMedia={originalMedia}
          isCampaignRunning={isCampaignRunning}
          readonly={readonly}
          onAddToSlot={onAddToSlot}
          onAddLibraryToSlot={onAddLibraryToSlot}
          onReplaceLibraryToSlot={onReplaceLibraryToSlot}
          onReplaceInSlot={onReplaceInSlot}
          onRemoveFromSlot={onRemoveFromSlot}
          setSlotError={setSlotError}
          isUploading={isUploading}
          isGenerating={isGenerating}
          isReady={isReady}
          requiredSlotRatios={requiredSlotRatios}
          isGoogleOnly={isGoogleOnly}
          isGoogleSelected={isGoogleSelected}
          googleExactRatios={googleExactRatios}
          showValidationErrors={showValidationErrors}
          missingGoogleExactRatios={missingGoogleExactRatios}
        />
      )}

      {selectiveMode && (
        <div
          className={classnames('media-list-card__selective-mode-overlay', {
            'media-list-card__selective-mode-overlay--selected': isSelected,
            'media-list-card__selective-mode-overlay--disabled': disableSelection,
          })}
          onClick={!disableSelection ? handleToggleSelection : undefined}
        >
          {!disableSelection && (
            <div
              className="media-list-card__selective-mode-overlay-checkbox"
              onClick={(event) => event.stopPropagation()}
            >
              <CustomRadioButton
                className="media-list-card__selective-mode-radio"
                checked={isSelected}
                onChange={handleToggleSelection}
                label={item.filename || item.name}
              />
            </div>
          )}
        </div>
      )}

      {!isUploading && !isHydrating && !selectiveMode  && !isError && !isGenerating && !readonly && (
        <div className="media-list-card__actions" ref={menuContainerRef}>
          <button
            className="dots-menu-btn"
            type="button"

            onClick={(event) => {
              event.stopPropagation()
              setIsMenuOpen((open) => !open)
            }}
          >
            <DotsMenuIcon />
          </button>

          {isMenuOpen && <div
            className="media-action-menu-popper"
          >
            <ActionMenu
              actionMenuItems={actionMenuItems}
              setIsMenuOpen={setIsMenuOpen}
            />
          </div>}
        </div>
      )}
    </div>
  )
}

MediaListCard.propTypes = {
  item: PropTypes.object.isRequired,
  selectiveMode: PropTypes.bool,
  disableSelection: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  isReady: PropTypes.bool,
  onPreviewClick: PropTypes.func,
  onViewVariants: PropTypes.func,
  showLabel: PropTypes.bool,
  mediaLabel: PropTypes.shape({
    variant: PropTypes.string,
    headline: PropTypes.string,
    variantsCount: PropTypes.number,
    bothSelected: PropTypes.bool,
  }),
  onRemove: PropTypes.func,
  onRemoveSubGroup: PropTypes.func,
  subGroupItems: PropTypes.arrayOf(PropTypes.object),
  subGroupId: PropTypes.string,
  originalId: PropTypes.string,
  originalMedia: PropTypes.object,
  isCampaignRunning: PropTypes.bool,
  readonly: PropTypes.bool,
  onAddToSlot: PropTypes.func,
  onAddLibraryToSlot: PropTypes.func,
  onReplaceLibraryToSlot: PropTypes.func,
  onReplaceInSlot: PropTypes.func,
  onRemoveFromSlot: PropTypes.func,
  setSlotError: PropTypes.func,
  requiredSlotRatios: PropTypes.arrayOf(PropTypes.string),
  isGoogleOnly: PropTypes.bool,
  isGoogleSelected: PropTypes.bool,
  googleExactRatios: PropTypes.arrayOf(PropTypes.string),
  showValidationErrors: PropTypes.bool,
  missingGoogleExactRatios: PropTypes.arrayOf(PropTypes.string),
}